/**
 * Fortrengningskart for brytning i glasskanten.
 *
 * Liquid Glass er brytning, ikke uskarphet. En uskarp, halvgjennomsiktig
 * plate demper bare det som ligger bak; ekte glass *bøyer* det — innholdet
 * forskyves, komprimeres og krummer seg nær kanten, mens midten står rolig.
 *
 * Trikset er at man ikke sporer lys i tre dimensjoner. Man lager et
 * todimensjonalt kart der hver piksel sier hvor langt og i hvilken retning
 * pikselen bak skal hentes fra:
 *
 *   rød kanal   = forskyvning i x
 *   grønn kanal = forskyvning i y
 *   128         = ingen forskyvning
 *   0 og 255    = maksimalt i hver retning
 *
 * `feDisplacementMap` leser kartet og flytter bakgrunnen deretter. Fordi man
 * ser rett på en flat skjerm, er den plane tilnærmingen visuelt ikke til å
 * skille fra ekte brytning.
 *
 * Se `Prosjektet logg-flat design (1)/uploads/liquid-glass-spec.md`.
 */

export interface BezelOptions {
  readonly width: number;
  readonly height: number;
  /** Hjørneradius på flaten. */
  readonly radius: number;
  /** Bredden på fasettkanten, der krumningen sitter. Midten er flat. */
  readonly bezel: number;
  /** Største forskyvning i piksler. Tilsvarer `scale` på filteret. */
  readonly maxDisplacement: number;
}

/**
 * Avstand til kanten av et avrundet rektangel. Negativ utenfor, positiv inne.
 * Dette er standard avstandsfunksjon for formen, og den gir oss både hvor
 * nær kanten vi er og hvilken vei kanten vender.
 */
function distanceInside(
  x: number, y: number, w: number, h: number, r: number,
): number {
  const hw = w / 2;
  const hh = h / 2;
  const qx = Math.abs(x - hw) - (hw - r);
  const qy = Math.abs(y - hh) - (hh - r);
  const ax = Math.max(qx, 0);
  const ay = Math.max(qy, 0);
  const outside = Math.sqrt(ax * ax + ay * ay);
  const inside = Math.min(Math.max(qx, qy), 0);
  // Positiv verdi betyr innenfor formen.
  return -(outside + inside - r);
}

/**
 * Hvor sterkt fasetten bryter, som funksjon av avstanden inn fra kanten.
 *
 * `t = 0` er ytterst, `t = 1` er der fasetten går over i den flate midten.
 *
 * Den konvekse superellipsen `⁴√(1 − (1−t)⁴)` beskriver formen på glasset,
 * men den kan ikke brukes direkte: tangenten er loddrett helt ytterst, så
 * hele forskyvningen havner i et bånd på to–tre piksler og forsvinner. Her
 * brukes derfor en dempet falloff som holder brytningen synlig gjennom hele
 * fasetten, sterkest ytterst.
 */
function refractionStrength(t: number): number {
  const u = 1 - Math.min(1, Math.max(0, t));
  return Math.pow(u, 1.6);
}

/**
 * Bygger kartet og gir det tilbake som en data-URI.
 *
 * Kalles én gang per størrelse. For et ark på 340 × 600 er det rundt 200 000
 * piksler, og det tar noen få millisekunder.
 */
export function buildDisplacementMap(opts: BezelOptions): string | null {
  const { width, height, radius, bezel, maxDisplacement } = opts;
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  if (w < 8 || h < 8 || maxDisplacement <= 0) return null;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (ctx === null) return null;

  const img = ctx.createImageData(w, h);
  const data = img.data;
  const r = Math.min(radius, Math.min(w, h) / 2);
  const band = Math.max(1, Math.min(bezel, Math.min(w, h) / 2 - 1));
  // Skrittlengde for å finne kantens retning numerisk.
  const eps = 1;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const d = distanceInside(x + 0.5, y + 0.5, w, h, r);

      // Utenfor formen, eller inne på den flate midten: ingen forskyvning.
      if (d <= 0 || d >= band) {
        data[i] = 128;
        data[i + 1] = 128;
        data[i + 2] = 128;
        data[i + 3] = 255;
        continue;
      }

      /*
       * Kantens retning, funnet ved å derivere avstandsfunksjonen numerisk.
       * Gradienten peker innover, siden avstanden vokser mot midten.
       *
       * Forskyvningen peker **innover**, ikke utover. Det er ikke en
       * smakssak: `backdrop-filter` har bare flatens egen bakgrunn å hente
       * fra, og et oppslag utenfor kanten gir ingen piksler — nettleseren
       * gjentar da kantpikselen, og resultatet blir utsmurt tekst i stedet
       * for brutt glass. Innover er det alltid noe å hente, og kanten leses
       * som en linse som trekker innholdet utover mot rammen.
       */
      const dx = distanceInside(x + 0.5 + eps, y + 0.5, w, h, r)
        - distanceInside(x + 0.5 - eps, y + 0.5, w, h, r);
      const dy = distanceInside(x + 0.5, y + 0.5 + eps, w, h, r)
        - distanceInside(x + 0.5, y + 0.5 - eps, w, h, r);
      const len = Math.hypot(dx, dy) || 1;
      const nx = dx / len;
      const ny = dy / len;

      const magnitude = refractionStrength(d / band);

      data[i] = Math.round(128 + nx * magnitude * 127);
      data[i + 1] = Math.round(128 + ny * magnitude * 127);
      data[i + 2] = 128;
      data[i + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL("image/png");
}

/**
 * Klarer motoren å bruke et SVG-filter som `backdrop-filter`?
 *
 * Bare Chromium gjør det i dag. WebKit-feil 245510 har stått åpen siden 2022,
 * og på trunk krasjer den GPU-prosessen i stedet for å rendre. Safari får
 * derfor en egen variant — se `.lg--flat` i overlays.css.
 *
 * `CSS.supports` kan ikke brukes: Safari svarer ja og rendrer likevel ikke.
 * Når WebKit slipper rettelsen, er det denne funksjonen som skal endres.
 */
export function supportsBackdropSvgFilter(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent;
  const isWebKitOnly = /\b(iPad|iPhone|iPod)\b/.test(ua)
    || (/Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|Android/.test(ua));
  if (isWebKitOnly) return false;
  return CSS.supports("backdrop-filter", "url(#x)")
    || CSS.supports("-webkit-backdrop-filter", "url(#x)");
}

/* ------------------------------------------------------------------ */

/**
 * Kontinuerlige hjørner.
 *
 * `corner-shape: superellipse()` finnes bare i Chromium. Spesifikasjonen
 * kaller rene sirkelbuer en anti-pattern, så formen tegnes i stedet som en
 * maske og virker da overalt.
 *
 * Kurven er superellipsen |x|ⁿ + |y|ⁿ = 1. `n = 2` er sirkelen; høyere `n`
 * trekker hjørnet nærmere det rette og gir det myke, kontinuerlige preget.
 * `n = 3,5` svarer til prototypens `superellipse(1.8)`.
 */
const SUPERELLIPSE_N = 3.5;
/** Punkter per hjørne. Nok til at kanten er glatt i disse størrelsene. */
const CORNER_STEPS = 14;

export interface CornerRadii {
  readonly topLeft: number;
  readonly topRight: number;
  readonly bottomRight: number;
  readonly bottomLeft: number;
}

/**
 * Punktene i ett hjørne, samplet langs superellipsen.
 *
 * Kurven går fra kanten som kommer inn, til kanten som går ut, og buler mot
 * det spisse ytterhjørnet — den skal aldri treffe det. `X` og `Y` er
 * normaliserte avstander fra hjørnets sentrum, altså punktet som ligger `r`
 * inn fra begge kantene.
 */
function cornerPoints(r: number, place: (X: number, Y: number) => [number, number]): string {
  if (r <= 0.5) {
    const [x, y] = place(0, 0);
    return `L ${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  const out: string[] = [];
  for (let i = 0; i <= CORNER_STEPS; i++) {
    const theta = (i / CORNER_STEPS) * (Math.PI / 2);
    const X = Math.pow(Math.cos(theta), 2 / SUPERELLIPSE_N);
    const Y = Math.pow(Math.sin(theta), 2 / SUPERELLIPSE_N);
    const [x, y] = place(X, Y);
    out.push(`L ${x.toFixed(2)} ${y.toFixed(2)}`);
  }
  return out.join(" ");
}

/**
 * Bygger en maske i form av flaten, som en data-URI.
 *
 * Radiene leses per hjørne, fordi ikke alle flater er like avrundet — menyen
 * er bare rundet øverst til venstre.
 */
export function buildSquircleMask(
  width: number, height: number, radii: CornerRadii,
): string {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const cap = Math.min(w, h) / 2;
  const tl = Math.min(radii.topLeft, cap);
  const tr = Math.min(radii.topRight, cap);
  const br = Math.min(radii.bottomRight, cap);
  const bl = Math.min(radii.bottomLeft, cap);

  // Hvert hjørne skrives ut eksplisitt. Fortegnsjonglering i én felles
  // funksjon var nettopp det som ga flikene.
  const d = [
    `M 0 ${tl.toFixed(2)}`,
    cornerPoints(tl, (X, Y) => [tl - tl * X, tl - tl * Y]),
    `L ${(w - tr).toFixed(2)} 0`,
    cornerPoints(tr, (X, Y) => [w - tr + tr * Y, tr - tr * X]),
    `L ${w} ${(h - br).toFixed(2)}`,
    cornerPoints(br, (X, Y) => [w - br + br * X, h - br + br * Y]),
    `L ${bl.toFixed(2)} ${h}`,
    cornerPoints(bl, (X, Y) => [bl - bl * Y, h - bl + bl * X]),
    "Z",
  ].join(" ");

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" `
    + `viewBox="0 0 ${w} ${h}"><path d="${d}" fill="#000"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * Mikrokorn.
 *
 * Ren uskarphet leser som plast. Et nesten usynlig korn gir flaten overflate.
 * Lages én gang og deles av alle flatene.
 */
export function grainTexture(): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">`
    + `<filter id="g"><feTurbulence type="fractalNoise" baseFrequency="0.9" `
    + `numOctaves="2" stitchTiles="stitch"/>`
    + `<feColorMatrix type="saturate" values="0"/></filter>`
    + `<rect width="120" height="120" filter="url(%23g)" opacity="0.5"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
